import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";
import GarageFAQ from "@/components/GarageFAQ";
import DriverDesigner from "@/components/DriverDesigner";
import SidekickDesigner from "@/components/SidekickDesigner";
import { drawCar } from "@/components/game/characters";
import { Wrench, Zap, Shield, Target, Bot, Paintbrush, Coins, CheckCircle2, Sparkles, Palette, Lock, User } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const GARAGE_BG =
  "https://images.unsplash.com/photo-1760688963190-71286798f839?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxzcG9ydHMlMjBjYXIlMjBkYXJrJTIwZ2FyYWdlfGVufDB8fHx8MTc3Nzc1NjAyM3ww&ixlib=rb-4.1.0&q=85";

const COLOR_PRESETS = [
  "#FF3B30", "#FFCC00", "#00A86B", "#007AFF", "#FF1493",
  "#E6B422", "#1E90FF", "#C8102E", "#FF6B35", "#FFFFFF",
];

// Compute the hue (degrees, 0..360) of a hex color. Returns null for greyscale.
function hexToHue(hex) {
  if (!hex || hex.length < 4) return null;
  const m = hex.replace("#", "");
  const r = parseInt(m.substring(0, 2), 16) / 255;
  const g = parseInt(m.substring(2, 4), 16) / 255;
  const b = parseInt(m.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  if (max === min) return 0;
  const d = max - min;
  let h;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return Math.round((h * 60 + 360) % 360);
}

// Each car kind has a "natural" body hue baked into its hand-drawn sprite.
// Hue-rotate works as an OFFSET, so we subtract the natural hue from the
// desired hue to get the right CSS filter delta.
const NATURAL_HUE = {
  cruiser: 120,  // green pickup
  knight:  220,  // dark blue muscle car
  tank:    80,   // olive-green tank
  toon:    50,   // yellow taxi
  hotrod:  0,    // red dragster
};

function carColorFilter(kind, bodyColor) {
  const desired = hexToHue(bodyColor);
  if (desired == null) return "saturate(0.4) brightness(1.05)";
  const natural = NATURAL_HUE[kind] ?? 0;
  const delta = (desired - natural + 360) % 360;
  // Slightly boost saturation so picked colors pop against the muted base art.
  return `hue-rotate(${delta}deg) saturate(1.25)`;
}

const UPGRADE_ICONS = {
  rocket_engine: Zap,
  reinforced_armor: Shield,
  missile_pack: Target,
  missile_mega: Target,
  rocket_boost_pack: Zap,
  monkey_bomb: Bot,
  monkey_bomb_pack: Bot,
  full_repair: Wrench,
};

export default function Garage() {
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [body, setBody] = useState("#FF3B30");
  const [accent, setAccent] = useState("#FFCC00");
  const [driver, setDriver] = useState(null);

  const load = async () => {
    const res = await axios.get(`${BACKEND_URL}/api/garage`, { withCredentials: true });
    setData(res.data);
    setName(res.data.car.name);
    setBody(res.data.car.body_color);
    setAccent(res.data.car.accent_color);
  };

  useEffect(() => {
    load();
  }, []);

  const saveCustomization = async () => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/garage/customize`,
        { name, body_color: body, accent_color: accent },
        { withCredentials: true }
      );
      toast.success("Car saved to your garage.");
      await load();
    } catch (e) {
      toast.error("Couldn't save. Try again.");
    }
  };

  const buyUpgrade = async (id) => {
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/garage/upgrade`,
        { upgrade_id: id },
        { withCredentials: true }
      );
      toast.success(`Applied: ${data.upgrades[id].name}`);
      setData((prev) => ({ ...prev, car: res.data.car, user: { ...prev.user, currency: res.data.currency } }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upgrade failed");
    }
  };

  const buyCosmetic = async (id) => {
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/garage/cosmetic/buy`,
        { cosmetic_id: id },
        { withCredentials: true }
      );
      toast.success(`Unlocked: ${data.cosmetics[id].name}`);
      setData((prev) => ({
        ...prev,
        unlocked_cosmetics: res.data.unlocked_cosmetics,
        user: { ...prev.user, currency: res.data.currency },
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't buy cosmetic");
    }
  };

  const applyPaint = async (id) => {
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/garage/cosmetic/paint`,
        { cosmetic_id: id },
        { withCredentials: true }
      );
      toast.success(`Painted: ${data.cosmetics[id].name}`);
      setData((prev) => ({ ...prev, car: res.data.car }));
      setBody(res.data.car.body_color);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't paint");
    }
  };

  const applyTrail = async (id) => {
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/garage/cosmetic/trail`,
        { cosmetic_id: id },
        { withCredentials: true }
      );
      toast.success(id ? `Trail: ${data.cosmetics[id].name}` : "Trail cleared");
      setData((prev) => ({ ...prev, active_trail: res.data.active_trail }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't set trail");
    }
  };

  const repair = async () => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/garage/repair`, {}, { withCredentials: true });
      toast.success(`Repaired for ${res.data.cost} credits.`);
      setData((prev) => ({ ...prev, car: res.data.car, user: { ...prev.user, currency: res.data.currency } }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Can't repair");
    }
  };

  const selectCar = async (templateId) => {
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/garage/select`,
        { template_id: templateId },
        { withCredentials: true }
      );
      toast.success(`Switched to ${res.data.car.name}.`);
      setData((prev) => ({ ...prev, car: res.data.car }));
      setName(res.data.car.name);
      setBody(res.data.car.body_color);
      setAccent(res.data.car.accent_color);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't switch vehicle");
    }
  };

  if (!data) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="label-kicker">Loading garage…</div>
        </div>
      </AppLayout>
    );
  }

  const { car, user, upgrades, templates, cosmetics, unlocked_cosmetics, active_trail } = data;
  const damage = (car.max_hp || 100) - (car.hp || 0);
  const unlockedSet = new Set(unlocked_cosmetics || []);
  const catLabels = {
    performance: "Performance",
    weapons: "Weapons",
    maintenance: "Maintenance",
  };
  const grouped = {};
  Object.entries(upgrades).forEach(([id, u]) => {
    const cat = u.category || "performance";
    (grouped[cat] = grouped[cat] || []).push([id, u]);
  });

  return (
    <AppLayout>
      <div className="relative">
        {/* Hero */}
        <div className="relative h-[300px] overflow-hidden border-b border-white/10">
          <img src={GARAGE_BG} alt="garage" className="absolute inset-0 w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-[#0A0A0A]" />
          <div className="relative h-full flex items-end px-6 lg:px-12 pb-10">
            <div>
              <div className="label-kicker mb-2">The Bay</div>
              <h1 className="font-heading text-5xl lg:text-6xl">Garage</h1>
              <div className="mt-3 flex items-center gap-2 text-[#FFCC00]">
                <Coins className="w-4 h-4" />
                <span className="font-mono" data-testid="garage-currency">
                  {user.currency.toLocaleString()} credits
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bento */}
        <div className="px-6 lg:px-12 py-10 grid grid-cols-1 lg:grid-cols-12 gap-6">

          {/* Vehicle picker — 5 cars */}
          {templates && (
            <div className="lg:col-span-12 surface-solid p-6" data-testid="garage-vehicle-picker">
              <div className="flex items-baseline justify-between mb-4">
                <div>
                  <div className="label-kicker">Vehicle bay</div>
                  <div className="font-heading text-3xl mt-1">Choose your ride</div>
                </div>
                <div className="font-mono text-xs text-white/40">{Object.keys(templates).length} VEHICLES · CURRENT · {(car.kind || "cruiser").toUpperCase()}</div>
              </div>
              <BuddyPanel onSaved={load} />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
                {Object.entries(templates).map(([id, t]) => {
                  const active = (car.kind || "cruiser") === id;
                  // Badge-gated cars (Golden Viper) — show as visually
                  // locked if the user doesn't own the required badge.
                  // Click is allowed (server returns 403 with a friendly
                  // message) but the card is dimmed + tagged.
                  const lockedBadge = t.locked_by_badge;
                  const userBadges = user?.badges || [];
                  const locked = lockedBadge && !userBadges.includes(lockedBadge);
                  return (
                    <button
                      key={id}
                      data-testid={`garage-select-${id}`}
                      onClick={() => selectCar(id)}
                      disabled={active}
                      className={`text-left flex flex-col p-4 border transition-all duration-150 relative ${
                        active
                          ? "border-[#FFCC00] bg-[#FFCC00]/10 cursor-default"
                          : locked
                            ? "border-[#FFCC00]/40 bg-black/30 hover:border-[#FFCC00]"
                            : "border-white/10 hover:border-[#FF3B30] hover:bg-[#FF3B30]/5"
                      }`}
                    >
                      {locked && (
                        <div className="absolute top-2 right-2 text-[9px] tracking-[0.2em] uppercase text-[#FFCC00] flex items-center gap-1 px-1.5 py-0.5 bg-[#FFCC00]/15 border border-[#FFCC00]/40">
                          🔒 LOCKED
                        </div>
                      )}
                      <CarThumb kind={id} />
                      <div className="mt-3 font-heading text-base leading-tight">{t.name}</div>
                      <div className="text-[10px] mt-1 text-white/50 leading-snug min-h-[28px]">{t.blurb}</div>
                      <div className="mt-3 grid grid-cols-3 gap-1">
                        <Pip label="SPD" v={t.top_speed} />
                        <Pip label="ARM" v={t.armor_rating} />
                        <Pip label="DMG" v={t.damage_rating} />
                      </div>
                      {locked && (
                        <div className="mt-2 text-[9px] tracking-widest uppercase text-[#FFCC00]/80 leading-tight">
                          Earn the {lockedBadge} badge
                        </div>
                      )}
                      {active && (
                        <div className="mt-3 text-[10px] tracking-widest text-[#FFCC00] flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> ACTIVE
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Car preview */}
          <div className="lg:col-span-8 surface-solid p-8 min-h-[420px] relative grain overflow-hidden">
            <div className="absolute top-4 right-4 label-kicker">Live preview</div>

            <CarPreview body={body} accent={accent} kind={car.kind} />

            <div className="relative mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="label-kicker">Call sign</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="garage-car-name"
                  className="mt-2 w-full bg-black border border-white/15 focus:border-[#FF3B30] outline-none px-4 py-3 font-heading text-xl"
                  placeholder="Your car's name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <ColorPicker label="Body" color={body} setColor={setBody} testid="garage-body-color" />
                <ColorPicker label="Accent" color={accent} setColor={setAccent} testid="garage-accent-color" />
              </div>
            </div>

            <button
              onClick={saveCustomization}
              data-testid="garage-save-customization"
              className="btn-primary mt-6 flex items-center gap-2"
            >
              <Paintbrush className="w-4 h-4" /> Save Look
            </button>
          </div>

          {/* Stats / HP */}
          <div className="lg:col-span-4 space-y-6">
            <div className="surface-solid p-6">
              <div className="label-kicker">Chassis status</div>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="font-heading text-4xl" data-testid="garage-hp-value">
                  {car.hp}
                </span>
                <span className="text-white/40 font-mono">/ {car.max_hp}</span>
              </div>
              <div className="mt-3 h-2 bg-white/10">
                <div
                  className="h-full bg-gradient-to-r from-[#FF3B30] to-[#FFCC00]"
                  style={{ width: `${(car.hp / car.max_hp) * 100}%` }}
                />
              </div>
              <button
                onClick={repair}
                disabled={damage === 0}
                data-testid="garage-repair-btn"
                className="mt-5 w-full border border-white/15 hover:border-[#34C759] hover:text-[#34C759] py-3 text-xs tracking-[0.3em] uppercase transition-colors duration-150 disabled:opacity-40 disabled:hover:border-white/15 disabled:hover:text-white flex items-center justify-center gap-2"
              >
                <Wrench className="w-3.5 h-3.5" />
                {damage === 0 ? "Fully repaired" : `Repair · ${damage * 3} credits`}
              </button>
            </div>

            <div className="surface-solid p-6">
              <div className="label-kicker">Tune stats</div>
              <div className="mt-4 space-y-3">
                <LevelBar label="Speed" level={car.speed_level} max={5} />
                <LevelBar label="Armor" level={car.armor_level} max={5} />
              </div>
              <div className="mt-4 pt-4 border-t border-white/10 text-xs text-white/60">
                <div className="label-kicker mb-2">Weapons loadout</div>
                <div className="grid grid-cols-3 gap-2">
                  <WeaponPill icon={Target} label="Missiles" count={car.weapons?.missile ?? 0} />
                  <WeaponPill icon={Zap} label="Boost" count={car.weapons?.rocket_boost ?? 0} />
                  <WeaponPill icon={Bot} label="Monkey" count={car.weapons?.monkey_bomb ?? 0} />
                </div>
              </div>
            </div>
          </div>

          {/* Shop — grouped by category */}
          <div className="lg:col-span-12 surface-solid p-6" data-testid="garage-shop">
            <div className="flex items-baseline justify-between mb-5">
              <div>
                <div className="label-kicker">Upgrade bay</div>
                <div className="font-heading text-3xl mt-1">Shop</div>
              </div>
              <div className="font-mono text-xs text-white/40">
                {Object.keys(upgrades).length} ITEMS
              </div>
            </div>

            {["performance", "weapons", "maintenance"].map((cat) => {
              const items = grouped[cat] || [];
              if (items.length === 0) return null;
              return (
                <div key={cat} className="mb-6 last:mb-0">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="label-kicker text-[#FF3B30]">{catLabels[cat]}</span>
                    <span className="h-px flex-1 bg-white/10" />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {items.map(([id, u]) => {
                      const Icon = UPGRADE_ICONS[id] ?? Zap;
                      const canAfford = user.currency >= u.price;
                      const atMax = u.stat && car[u.stat] >= u.max_level;
                      const fullyRepaired = u.repair && damage === 0;
                      const disabled = !canAfford || atMax || fullyRepaired;
                      return (
                        <button
                          key={id}
                          data-testid={`garage-upgrade-${id}`}
                          disabled={disabled}
                          onClick={() => buyUpgrade(id)}
                          className={`group flex flex-col items-start p-4 border transition-all duration-150 text-left ${
                            disabled
                              ? "border-white/10 opacity-40 cursor-not-allowed"
                              : "border-white/10 hover:border-[#FF3B30] hover:bg-[#FF3B30]/5"
                          }`}
                        >
                          <Icon className="w-5 h-5 text-[#FF3B30]" />
                          <div className="mt-3 font-heading text-base leading-tight">{u.name}</div>
                          {u.desc && (
                            <div className="mt-1 text-[10px] text-white/50 leading-snug">{u.desc}</div>
                          )}
                          <div className="mt-3 flex items-center gap-1 text-[#FFCC00] text-sm">
                            <Coins className="w-3 h-3" />
                            <span className="font-mono">{u.price.toLocaleString()}</span>
                          </div>
                          {atMax && (
                            <div className="mt-2 text-[10px] tracking-widest text-[#34C759] flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" /> MAX LEVEL
                            </div>
                          )}
                          {fullyRepaired && (
                            <div className="mt-2 text-[10px] tracking-widest text-[#34C759] flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" /> FULL HP
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Cosmetics — paints + trails */}
          {cosmetics && (
            <div className="lg:col-span-12 surface-solid p-6" data-testid="garage-cosmetics">
              <div className="flex items-baseline justify-between mb-5">
                <div>
                  <div className="label-kicker">Vanity bay</div>
                  <div className="font-heading text-3xl mt-1">Cosmetics</div>
                </div>
                <div className="font-mono text-xs text-white/40">
                  {unlockedSet.size}/{Object.keys(cosmetics).length} UNLOCKED
                </div>
              </div>

              {/* Paints */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <Palette className="w-3.5 h-3.5 text-[#FFCC00]" />
                  <span className="label-kicker text-[#FFCC00]">Premium Paints</span>
                  <span className="h-px flex-1 bg-white/10" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  {Object.entries(cosmetics)
                    .filter(([, c]) => c.type === "paint")
                    .map(([id, c]) => (
                      <CosmeticCard
                        key={id}
                        id={id}
                        cos={c}
                        owned={unlockedSet.has(id)}
                        canAfford={user.currency >= c.price}
                        active={body === c.color}
                        onBuy={buyCosmetic}
                        onApply={applyPaint}
                        applyLabel="Paint"
                      />
                    ))}
                </div>
              </div>

              {/* Trails */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-3.5 h-3.5 text-[#00F0FF]" />
                  <span className="label-kicker text-[#00F0FF]">Exhaust Trails</span>
                  <span className="h-px flex-1 bg-white/10" />
                  {active_trail && (
                    <button
                      onClick={() => applyTrail(null)}
                      data-testid="garage-trail-clear"
                      className="text-[10px] tracking-[0.3em] uppercase text-white/50 hover:text-[#FF3B30]"
                    >
                      Clear trail
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  {Object.entries(cosmetics)
                    .filter(([, c]) => c.type === "trail")
                    .map(([id, c]) => (
                      <CosmeticCard
                        key={id}
                        id={id}
                        cos={c}
                        owned={unlockedSet.has(id)}
                        canAfford={user.currency >= c.price}
                        active={active_trail === id}
                        onBuy={buyCosmetic}
                        onApply={applyTrail}
                        applyLabel="Equip"
                      />
                    ))}
                </div>
              </div>
            </div>
          )}

          {/* Driver — used for boxing boss fights at the end of each city */}
          <div className="lg:col-span-12 surface-solid p-8 mt-2" data-testid="garage-driver-section">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-4 h-4 text-[#FFCC00]" />
              <div>
                <div className="label-kicker">Boxing portrait</div>
                <div className="font-heading text-2xl">Your Driver</div>
              </div>
              <div className="ml-auto text-[11px] text-white/50 max-w-md text-right">
                When you complete a city, you exit your car for a boxing boss fight. Pick how your driver looks.
              </div>
            </div>
            <DriverDesigner value={driver} onChange={setDriver} />
          </div>

          {/* FAQ — drivers' manual for new mechanics */}
          <div className="lg:col-span-12">
            <GarageFAQ />
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

function CarPreview({ body, accent, kind = "cruiser" }) {
  // Use the same drawCar() pipeline the race canvas uses, so what you
  // see here is exactly what shows up on the track. Renders body + accent
  // tints accurately, supports all chassis types.
  const cvsRef = React.useRef(null);
  React.useEffect(() => {
    const cvs = cvsRef.current;
    if (!cvs) return;
    const ctx = cvs.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    const cssW = 360;
    const cssH = 200;
    cvs.width = Math.floor(cssW * dpr);
    cvs.height = Math.floor(cssH * dpr);
    cvs.style.width = `${cssW}px`;
    cvs.style.height = `${cssH}px`;
    ctx.scale(dpr, dpr);
    // Enable smoothing for the preview so the upscaled tinted sprite
    // reads as a glossy, magazine-style render — pixelation only makes
    // sense at race-zoom; here we have plenty of room to be smooth.
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    let raf;
    const render = () => {
      ctx.clearRect(0, 0, cssW, cssH);
      ctx.save();
      // Slight hover float
      const t = performance.now() / 600;
      const float = Math.sin(t) * 4;
      ctx.translate(cssW / 2, cssH / 2 + float);
      // Scale the in-game car (drawn at 56 px wide) up for the preview
      ctx.scale(3.6, 3.6);
      drawCar(ctx, 0, 0, 0, body, accent, 1, kind);
      ctx.restore();
      raf = requestAnimationFrame(render);
    };
    render();
    return () => cancelAnimationFrame(raf);
  }, [body, accent, kind]);

  return (
    <div className="relative h-[200px] flex items-center justify-center">
      <canvas
        ref={cvsRef}
        className="drop-shadow-[0_0_30px_rgba(255,59,48,0.35)]"
        data-testid="garage-car-preview"
      />
    </div>
  );
}

function CarThumb({ kind }) {
  // For kinds without a baked PNG asset (e.g., viper, future cars), fall
  // back to a procedural canvas rendering of the bird's-eye drawCar.
  // Tracks whether the <img> errored once and swaps to canvas on retry.
  const [imgErrored, setImgErrored] = React.useState(false);
  const canvasRef = React.useRef(null);
  React.useEffect(() => {
    if (!imgErrored || !canvasRef.current) return;
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.imageSmoothingEnabled = false;
    // drawCar expects logical 0,0 = car centre. Translate so the car sits
    // dead-centre in the 100×80 thumbnail. Body/accent reflect the
    // viper's native colors (black body, gold accent).
    ctx.translate(c.width / 2, c.height / 2);
    ctx.scale(1.4, 1.4);
    drawCar(ctx, 0, 0, 0, "#0E0C12", "#E0B100", 1, kind);
  }, [imgErrored, kind]);
  return (
    <div className="h-[80px] w-full bg-black/40 border border-white/5 flex items-center justify-center">
      {imgErrored ? (
        <canvas
          ref={canvasRef}
          width={100}
          height={70}
          style={{ imageRendering: "pixelated", height: 70, width: "auto" }}
        />
      ) : (
        <img
          src={`${process.env.REACT_APP_BACKEND_URL}/api/static/sprites/car_${kind}_front.png?v=8`}
          alt={kind}
          onError={() => setImgErrored(true)}
          style={{ imageRendering: "pixelated", maxHeight: 70, width: "auto" }}
        />
      )}
    </div>
  );
}

// Single cosmetic tile — handles the three states (locked, owned, active)
// and flips between "Buy N credits" and "Paint / Equip".
function CosmeticCard({ id, cos, owned, canAfford, active, onBuy, onApply, applyLabel }) {
  const isRainbow = cos.color === "__rainbow__";
  const swatchStyle = isRainbow
    ? { background: "linear-gradient(90deg,#FF3B30,#FFCC00,#34C759,#00F0FF,#7B2CBF)" }
    : { background: cos.color };
  const state = active ? "active" : owned ? "owned" : "locked";
  const borderCls = {
    active: "border-[#FFCC00] bg-[#FFCC00]/5",
    owned: "border-white/30 hover:border-[#FFCC00]",
    locked: "border-white/10 hover:border-[#FF3B30]",
  }[state];
  const disabled = state === "locked" && !canAfford;

  return (
    <div
      className={`relative p-3 border transition-colors duration-150 ${borderCls}`}
      data-testid={`cosmetic-${id}`}
    >
      {state === "locked" && (
        <div className="absolute top-2 right-2 text-white/30">
          <Lock className="w-3 h-3" />
        </div>
      )}
      {state === "active" && (
        <div className="absolute top-2 right-2 text-[#FFCC00]">
          <CheckCircle2 className="w-3.5 h-3.5" />
        </div>
      )}
      <div
        className="w-full h-10 border border-white/10"
        style={swatchStyle}
      />
      <div className="mt-2 font-heading text-sm leading-tight">{cos.name}</div>
      {state === "locked" ? (
        <button
          onClick={() => onBuy(id)}
          disabled={disabled}
          data-testid={`cosmetic-buy-${id}`}
          className={`mt-2 w-full flex items-center justify-center gap-1 px-2 py-1.5 text-xs tracking-wider uppercase border transition-colors ${
            disabled
              ? "border-white/10 text-white/30 cursor-not-allowed"
              : "border-[#FFCC00] text-[#FFCC00] hover:bg-[#FFCC00] hover:text-black"
          }`}
        >
          <Coins className="w-3 h-3" />
          <span className="font-mono">BUY · {cos.price.toLocaleString()}</span>
        </button>
      ) : state === "owned" ? (
        <button
          onClick={() => onApply(id)}
          data-testid={`cosmetic-apply-${id}`}
          className="mt-2 w-full px-2 py-1.5 text-xs tracking-wider uppercase border border-white/20 hover:border-[#FFCC00] hover:text-[#FFCC00] transition-colors"
        >
          {applyLabel}
        </button>
      ) : (
        <div
          className="mt-2 w-full px-2 py-1.5 text-xs tracking-[0.2em] uppercase text-center text-[#FFCC00]"
          data-testid={`cosmetic-active-${id}`}
        >
          Active
        </div>
      )}
    </div>
  );
}

// Inline SVG silhouettes for each car kind. Kept lightweight for thumbnails.
function CarSilhouette({ body, accent, kind, className }) {
  if (kind === "knight") {
    return (
      <svg viewBox="0 0 300 120" className={className}>
        <rect x="20" y="50" width="240" height="40" rx="8" fill={body} stroke="#0A0A0A" strokeWidth="3" />
        <polygon points="60,50 110,30 200,30 240,50" fill={body} stroke="#0A0A0A" strokeWidth="3" />
        <rect x="120" y="35" width="70" height="18" fill="#1f2e4a" stroke="#0A0A0A" strokeWidth="2" />
        <rect x="220" y="58" width="22" height="22" fill="#0A0A0A" />
        <rect x="226" y="64" width="10" height="10" fill={accent}>
          <animate attributeName="x" values="220;238;220" dur="1.6s" repeatCount="indefinite" />
        </rect>
        <circle cx="70" cy="95" r="14" fill="#0A0A0A" stroke={accent} strokeWidth="2" />
        <circle cx="220" cy="95" r="14" fill="#0A0A0A" stroke={accent} strokeWidth="2" />
      </svg>
    );
  }
  if (kind === "tank") {
    return (
      <svg viewBox="0 0 300 120" className={className}>
        <rect x="30" y="20" width="240" height="14" fill="#1F1F1F" stroke="#0A0A0A" strokeWidth="3" />
        <rect x="30" y="86" width="240" height="14" fill="#1F1F1F" stroke="#0A0A0A" strokeWidth="3" />
        {Array.from({ length: 8 }).map((_, i) => (
          <rect key={`t${i}`} x={40 + i * 28} y={22} width={12} height={10} fill={accent} />
        ))}
        {Array.from({ length: 8 }).map((_, i) => (
          <rect key={`b${i}`} x={40 + i * 28} y={88} width={12} height={10} fill={accent} />
        ))}
        <rect x="40" y="36" width="220" height="48" rx="4" fill={body} stroke="#0A0A0A" strokeWidth="3" />
        <circle cx="150" cy="60" r="20" fill="#3F4A26" stroke="#0A0A0A" strokeWidth="3" />
        <rect x="170" y="55" width="80" height="10" fill="#0A0A0A" />
        <rect x="245" y="52" width="10" height="16" fill={accent} />
      </svg>
    );
  }
  if (kind === "toon") {
    return (
      <svg viewBox="0 0 300 120" className={className}>
        <ellipse cx="150" cy="65" rx="100" ry="42" fill={body} stroke="#0A0A0A" strokeWidth="3" />
        {/* checker stripe */}
        {Array.from({ length: 12 }).map((_, i) => (
          <rect key={i} x={55 + i * 16} y={60} width={16} height={10} fill={i % 2 === 0 ? accent : "transparent"} />
        ))}
        {/* big eyes */}
        <circle cx="200" cy="46" r="14" fill="#FFFFFF" stroke="#0A0A0A" strokeWidth="2.5" />
        <circle cx="200" cy="84" r="14" fill="#FFFFFF" stroke="#0A0A0A" strokeWidth="2.5" />
        <circle cx="204" cy="46" r="6" fill="#0A0A0A" />
        <circle cx="204" cy="84" r="6" fill="#0A0A0A" />
        {/* grin */}
        <path d="M120 88 Q150 102 180 88" fill="#FFFFFF" stroke="#0A0A0A" strokeWidth="2.5" />
      </svg>
    );
  }
  if (kind === "hotrod") {
    return (
      <svg viewBox="0 0 300 120" className={className}>
        <polygon points="40,40 220,38 260,60 220,82 40,80" fill={body} stroke="#0A0A0A" strokeWidth="3" />
        <rect x="40" y="55" width="220" height="10" fill={accent} />
        <rect x="120" y="30" width="60" height="22" rx="4" fill="#1f2e4a" stroke="#0A0A0A" strokeWidth="2" />
        <rect x="20" y="20" width="6" height="80" fill="#0A0A0A" />
        <rect x="22" y="22" width="2" height="76" fill={accent} />
        <circle cx="70" cy="95" r="14" fill="#0A0A0A" />
        <circle cx="220" cy="95" r="14" fill="#0A0A0A" />
      </svg>
    );
  }
  // cruiser default
  return (
    <svg viewBox="0 0 300 120" className={className}>
      <rect x="40" y="40" width="220" height="50" rx="6" fill={body} stroke={accent} strokeWidth="3" />
      <path d={`M70 40 L110 15 L190 15 L230 40 Z`} fill={body} stroke={accent} strokeWidth="3" />
      <rect x="115" y="20" width="70" height="22" fill="#111" stroke={accent} strokeWidth="2" />
      <circle cx="80" cy="95" r="15" fill="#111" stroke={accent} strokeWidth="3" />
      <circle cx="220" cy="95" r="15" fill="#111" stroke={accent} strokeWidth="3" />
      <rect x="250" y="55" width="10" height="20" fill={accent} />
    </svg>
  );
}

function Pip({ label, v }) {
  return (
    <div className="border border-white/10 px-1 py-0.5 text-center">
      <div className="text-[8px] tracking-widest text-white/40">{label}</div>
      <div className="font-heading text-xs">{"●".repeat(v) + "○".repeat(5 - v)}</div>
    </div>
  );
}

function ColorPicker({ label, color, setColor, testid }) {
  return (
    <div>
      <label className="label-kicker">{label}</label>
      <div className="mt-2 flex flex-wrap gap-1.5" data-testid={testid}>
        {COLOR_PRESETS.map((c) => (
          <button
            key={c}
            onClick={() => setColor(c)}
            className={`w-7 h-7 border transition-transform duration-150 ${
              color === c ? "border-white scale-110" : "border-white/20"
            }`}
            style={{ background: c }}
            aria-label={c}
          />
        ))}
      </div>
    </div>
  );
}

function LevelBar({ label, level, max }) {
  return (
    <div>
      <div className="flex items-center justify-between text-xs">
        <span className="label-kicker">{label}</span>
        <span className="font-mono text-white/60">L{level}/{max}</span>
      </div>
      <div className="mt-1 flex gap-1">
        {Array.from({ length: max }).map((_, i) => (
          <div key={i} className={`h-2 flex-1 ${i < level ? "bg-[#FF3B30]" : "bg-white/10"}`} />
        ))}
      </div>
    </div>
  );
}

function WeaponPill({ icon: Icon, label, count }) {
  return (
    <div className="border border-white/10 p-2">
      <div className="flex items-center gap-1 text-white/60">
        <Icon className="w-3 h-3" />
        <span className="text-[10px] tracking-widest">{label}</span>
      </div>
      <div className="font-heading text-xl mt-0.5">×{count}</div>
    </div>
  );
}

// BuddyPanel — always-available "Customize Buddy" CTA above the vehicle
// grid. Replaces the old in-race 30-second designer popup. Per user
// request (iter-49): "you only can activate the buddy in the garage and
// that is the only time you can customize the buddy and there is no time
// limit to customize it make sure it never pops up in games ever again."
function BuddyPanel({ onSaved }) {
  const [open, setOpen] = React.useState(false);
  const [info, setInfo] = React.useState({ credits: 0, has_design: false, design: null });
  const refresh = React.useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/sidekick/me`, { withCredentials: true });
      setInfo({
        credits: r.data?.credits || 0,
        has_design: !!r.data?.design,
        design: r.data?.design || null,
      });
    } catch (_e) { /* ignore */ }
  }, []);
  React.useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="mb-5 surface-glass border border-[#34C759]/30 p-5 flex items-center justify-between gap-4" data-testid="buddy-panel">
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 border border-[#34C759]/40 bg-[#34C759]/10 flex items-center justify-center">
          <Sparkles className="w-6 h-6 text-[#34C759]" />
        </div>
        <div>
          <div className="label-kicker text-[#34C759]">Wingman buddy</div>
          <div className="font-heading text-lg leading-tight">
            {info.has_design ? `Buddy ready — ${info.design?.name || "BUDDY"}` : "No buddy designed yet"}
          </div>
          <div className="text-[11px] text-white/50 leading-snug mt-0.5">
            {info.credits > 0
              ? `${info.credits} deploy${info.credits === 1 ? "" : "s"} banked. Buddy spawns automatically next race — no popup.`
              : "Earn a deploy credit by collecting 2 artifacts within 10s during a race."}
          </div>
        </div>
      </div>
      <button
        onClick={() => setOpen(true)}
        data-testid="buddy-customize-btn"
        className="px-5 py-2 border border-[#34C759] text-[#34C759] font-heading uppercase text-sm tracking-wider hover:bg-[#34C759]/10"
      >
        {info.has_design ? "Edit Buddy" : "Customize Buddy"}
      </button>
      {open && (
        <SidekickDesigner
          noTimer
          onComplete={async () => {
            await refresh();
            setOpen(false);
            toast.success("Buddy saved.");
            onSaved?.();
          }}
          onCancel={() => setOpen(false)}
        />
      )}
    </div>
  );
}
